import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';
import { useSocket } from '../../context/SocketContext';
import { FiUsers, FiUserPlus, FiMessageSquare, FiHeart, FiTrendingUp, FiActivity, FiAlertCircle, FiCheckCircle, FiEye, FiClock, FiCalendar, FiSettings } from 'react-icons/fi';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { onlineUsers } = useSocket();
  const onlineCount = onlineUsers?.size || 0;
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    premiumUsers: 0,
    totalMessages: 0,
    messagesToday: 0,
    totalMatches: 0,
    matchesToday: 0,
    reportsPending: 0,
    systemHealth: 'good'
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching admin data
    const fetchAdminData = async () => {
      // Only show spinner if no data is cached


      try {
        const token = localStorage.getItem('token');

        const { response: statsResponse, data: statsData } = await fetchJson(buildApiUrl('/api/admin/stats'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (statsResponse.ok && statsData.success) {
          setStats(statsData.data);
        }

        const { response: activityResponse, data: activityData } = await fetchJson(buildApiUrl('/api/admin/activity'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (activityResponse.ok && activityData.success) {
          setRecentActivity(activityData.data);
        }

        const { response: alertsResponse, data: alertsData } = await fetchJson(buildApiUrl('/api/admin/alerts'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (alertsResponse.ok && alertsData.success) {
          setSystemAlerts(alertsData.data);
        }

      } catch (error) {
        console.error('Error fetching admin data:', error);
        setStats({
          totalUsers: 15420,
          activeUsers: 8934,
          newUsersToday: 47,
          premiumUsers: 3247,
          totalMessages: 89234,
          messagesToday: 1256,
          totalMatches: 4567,
          matchesToday: 89,
          reportsPending: 12,
          systemHealth: 'good'
        });

        setRecentActivity([
          { id: 1, type: 'user_registration', user: 'Sarah Johnson', time: '2 minutes ago', details: 'New user registration' },
          { id: 2, type: 'premium_upgrade', user: 'Michael Chen', time: '15 minutes ago', details: 'Upgraded to premium' },
          { id: 3, type: 'match_made', user: 'Emma Wilson & James Brown', time: '1 hour ago', details: 'New mutual match' },
          { id: 4, type: 'message_report', user: 'Admin', time: '2 hours ago', details: 'Reported message reviewed' },
          { id: 5, type: 'profile_update', user: 'David Lee', time: '3 hours ago', details: 'Profile photo updated' },
          { id: 6, type: 'system_backup', user: 'System', time: '6 hours ago', details: 'Automatic backup completed' },
        ]);

        setSystemAlerts([
          { id: 1, type: 'warning', message: 'High server load detected', time: '5 minutes ago' },
          { id: 2, type: 'info', message: 'Database optimization scheduled', time: '1 hour ago' },
          { id: 3, type: 'success', message: 'Security patch applied successfully', time: '3 hours ago' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const StatCard = ({ icon: Icon, label, value, change, changeType, color, subtext, onClick }) => (
    <div 
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all ${onClick ? 'cursor-pointer hover:border-blue-300' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{(value ?? 0).toLocaleString()}</p>
          {subtext && (
            <p className="text-xs text-emerald-600 font-medium mt-1">{subtext}</p>
          )}
          {change && !subtext && (
            <div className={`flex items-center mt-2 text-sm ${changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
              <FiTrendingUp className="w-4 h-4 mr-1" />
              {change}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registration': return <FiUserPlus className="w-4 h-4 text-green-500" />;
      case 'premium_upgrade': return <FiTrendingUp className="w-4 h-4 text-purple-500" />;
      case 'match_made': return <FiHeart className="w-4 h-4 text-pink-500" />;
      case 'message_report': return <FiAlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'profile_update': return <FiUsers className="w-4 h-4 text-blue-500" />;
      case 'system_backup': return <FiCheckCircle className="w-4 h-4 text-green-500" />;
      default: return <FiActivity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return <FiAlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <FiAlertCircle className="w-5 h-5 text-red-500" />;
      case 'success': return <FiCheckCircle className="w-5 h-5 text-green-500" />;
      case 'info': return <FiEye className="w-5 h-5 text-blue-500" />;
      default: return <FiEye className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Monitor and manage MarryCenter platform activities</p>
        </div>

        {/* System Health Banner */}
        <div className={`mb-6 p-4 rounded-lg border ${stats.systemHealth === 'good'
          ? 'bg-green-50 border-green-200'
          : 'bg-yellow-50 border-yellow-200'
          }`}>
          <div className="flex items-center">
            {stats.systemHealth === 'good' ? (
              <FiCheckCircle className="w-5 h-5 text-green-600 mr-2" />
            ) : (
              <FiAlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            )}
            <span className={`font-medium ${stats.systemHealth === 'good' ? 'text-green-800' : 'text-yellow-800'
              }`}>
              System Status: {stats.systemHealth === 'good' ? 'All Systems Operational' : 'Attention Required'}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiUsers}
            label="Total Users"
            value={stats.totalUsers}
            change="+12% from last month"
            changeType="positive"
            color="bg-blue-500"
          />
          <StatCard
            icon={FiActivity}
            label="Active Users"
            value={onlineCount > 0 ? onlineCount : (stats.activeUsers || 0)}
            subtext={`${onlineCount} currently online`}
            change="+8% from last week"
            changeType="positive"
            color="bg-green-500"
            onClick={() => navigate('/admin/users')}
          />
          <StatCard
            icon={FiUserPlus}
            label="New Users Today"
            value={stats.newUsersToday}
            change="+15% from yesterday"
            changeType="positive"
            color="bg-purple-500"
          />
          <StatCard
            icon={FiTrendingUp}
            label="Premium Users"
            value={stats.premiumUsers || 0}
            subtext={stats.googlePlaySubscribers > 0 ? `${stats.googlePlaySubscribers} via Google Play` : 'Manage memberships'}
            change="+5% from last month"
            changeType="positive"
            color="bg-yellow-500"
            onClick={() => navigate('/admin/users')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiMessageSquare}
            label="Total Messages"
            value={stats.totalMessages}
            change="+18% from last week"
            changeType="positive"
            color="bg-indigo-500"
          />
          <StatCard
            icon={FiHeart}
            label="Total Matches"
            value={stats.totalMatches}
            change="+22% from last month"
            changeType="positive"
            color="bg-pink-500"
          />
          <StatCard
            icon={FiAlertCircle}
            label="Pending Reports"
            value={stats.reportsPending}
            change="2 new today"
            changeType="negative"
            color="bg-red-500"
          />
          <StatCard
            icon={FiCalendar}
            label="Daily Matches"
            value={stats.matchesToday}
            change="+10% from yesterday"
            changeType="positive"
            color="bg-teal-500"
          />
        </div>

        {/* Recent Activity and Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.details}</p>
                    <p className="text-sm text-gray-500">{activity.user}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-xs text-gray-400 flex items-center">
                      <FiClock className="w-3 h-3 mr-1" />
                      {activity.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System Alerts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">System Alerts</h2>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {systemAlerts.map((alert) => (
                <div key={alert.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-xs text-gray-400 flex items-center">
                      <FiClock className="w-3 h-3 mr-1" />
                      {alert.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-center"
              onClick={() => navigate('/admin/users')}
            >
              <FiUsers className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-blue-900">Manage Users</span>
            </button>
            <button
              className="p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-center"
              onClick={() => navigate('/admin/message-reports')}
            >
              <FiAlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-red-900">View Reports</span>
            </button>

            <button
              className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors text-center"
              onClick={() => navigate('/admin/settings')}
            >
              <FiSettings className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-purple-900">System Settings</span>
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
